import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ControlledSelect,
  ControlledTextField
} from 'components/shared/ControlledFields';
import { getPropertyMappings } from '../useGetAFASSettings';
import { toOptions } from 'utils/toOptions';
import { isEmpty, isNil, omit } from 'ramda';
import Skeleton from '@mui/material/Skeleton';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import {
  additionalSettingsSchema,
  createEntityFromKeyValueObject,
  createKeyValueObject
} from '../utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Entity } from '../types';
import { LoadingButton } from '@mui/lab';
import Box from '@mui/material/Box';
import { useSaveResource } from 'RestAPIs/useSaveResource';
import { AFAS_IMPORT_MAPPING } from 'RestAPIs/endpoints';

export const ImportMappingTab = ({
  entityMappings,
  isFetchingAFASImportMappings,
  defaultPropArray
}) => {
  const { t } = useTranslation('common');
  const entityId = useRef(entityMappings[0].id);
  const [propMappings, setPropMappings] =
    useState<Entity['propertyMappings']>(defaultPropArray);
  const { mutateAsync: save, isLoading } = useSaveResource({
    id: entityId.current,
    url: AFAS_IMPORT_MAPPING,
    invalidateQueryKey: ['entity-mappings']
  });

  const methods = useForm({
    defaultValues: additionalSettingsSchema.cast({
      entityMapping: 1,
      ...createKeyValueObject(propMappings)
    }),
    resolver: yupResolver(additionalSettingsSchema)
  });

  const { handleSubmit } = methods;

  const onSubmit = async (data) => {
    await save({
      propertyMappings: [
        ...createEntityFromKeyValueObject(omit(['entityMapping'], data))
      ]
    });
  };

  return !isFetchingAFASImportMappings ? (
    <>
      <MultiStepForm methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <MultiStepForm.Section title={t('importMappings')}>
          <FormFields
            entityId={entityId}
            entityMappings={entityMappings}
            currentItem={propMappings}
            setCurrentItemProps={setPropMappings}
          />
        </MultiStepForm.Section>
        <Box
          sx={{ paddingBottom: 3, display: 'flex', justifyContent: 'flex-end' }}
        >
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            color="primary"
          >
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm>
    </>
  ) : (
    <Skeleton />
  );
};

const FormFields = ({
  entityMappings,
  setCurrentItemProps,
  currentItem,
  entityId
}) => {
  const { t } = useTranslation('common');
  const { control, watch, reset } = useFormContext();

  const watchedEntity = watch('entityMapping');
  const fetchedEntityProps = useCallback(async (id) => {
    const response = await getPropertyMappings(id);
    return response.data.propertyMappings;
  }, []);

  useEffect(() => {
    if (!isNil(watchedEntity)) {
      fetchedEntityProps(watchedEntity)
        .then((r) => setCurrentItemProps(r))
        .finally(() => {
          reset({
            entityMapping: watchedEntity,
            ...createKeyValueObject(currentItem)
          });
        });
    }
  }, [watchedEntity]);

  return (
    <>
      <Box sx={{ maxWidth: 450 }}>
        <ControlledSelect
          handleSelect={(id) => {
            entityId.current = id;
          }}
          control={control}
          name="entityMapping"
          label={t('entityMapping')}
          options={toOptions(entityMappings ?? [], 'id', 'entity')}
        />
      </Box>
      {!isEmpty(currentItem) ? (
        currentItem.map((item, index: number) => {
          return (
            <ControlledTextField
              sx={{ maxWidth: 450 }}
              key={`${index}_prop`}
              control={control}
              name={item.internalName}
              label={item.internalName}
            />
          );
        })
      ) : (
        <></>
      )}
    </>
  );
};
